import { plural } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import { MediaManagerTemplateGroup } from 'modules/manager/domain/interfaces/media-manager-template-group';
import { TEMPLATE_GROUP_NOT_EMPTY_CODE, isTemplateGroupNotEmpty } from 'modules/manager/domain/template-name-conflict';
import { useDeleteMediaManagerTemplateGroupMutation } from 'modules/manager/store/media-manager-api';
import { useCallback, useState } from 'react';
import Modal from 'ud-ui/components/modal';

/** The group list and the championship «Медиа» tab refetch by the mutation's tags (IMGM-1515). */
function useDeleteTemplateGroupModal() {
  const { t } = useLingui();
  const [deleteGroup, { isLoading }] = useDeleteMediaManagerTemplateGroupMutation();
  const [group, setGroup] = useState<MediaManagerTemplateGroup | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback((next: MediaManagerTemplateGroup) => {
    setGroup(next);
    setIsOpen(true);
  }, []);
  const close = useCallback(() => setIsOpen(false), []);

  const onSubmit = useCallback(async () => {
    if (!group) return;
    try {
      // A non-empty group is refused with 22412 unless its templates go with it.
      await deleteGroup({ id: group.id, deleteTemplates: group.templatesCount > 0 }).unwrap();
      close();
    } catch (error) {
      console.error('Failed to delete media manager template group:', error);
      if (isTemplateGroupNotEmpty(error)) {
        console.error(`Group delete refused with ${TEMPLATE_GROUP_NOT_EMPTY_CODE}`);
      }
    }
  }, [group, deleteGroup, close]);

  const groupName = group?.name;
  const templatesCount = group?.templatesCount ?? 0;
  const templatesCountLabel = plural(templatesCount, {
    one: '# шаблон',
    few: '# шаблона',
    many: '# шаблонов',
    other: '# шаблонов',
  });
  const description =
    templatesCount > 0 ? t`В группе ${templatesCountLabel}. Они будут удалены вместе с группой` : t`Это действие необратимо.`;

  return {
    open,
    close,
    modal: (
      <Modal.Confirm
        show={isOpen}
        title={groupName ? t`Удалить группу «${groupName}»?` : t`Удалить группу?`}
        description={description}
        isLoading={isLoading}
        onConfirm={onSubmit}
        onCancel={close}
      />
    ),
  };
}

export default useDeleteTemplateGroupModal;
